import { useEffect, useRef } from 'react';
import { AxiosResponse } from 'axios';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { realmMigrationQueryKeys } from 'RealmModule/query/realmMigrationQueryKeys';
import {
  RealmMigrationStep,
  RealmMigrationSummary,
  getRealmMigrationSteps,
  listRealmMigrations,
} from 'RealmModule/services/realmMigrationApi';

/** Statuses that will never change again, so polling can stop. */
const TERMINAL = ['Success', 'Failed'];

export const isTerminalMigrationStatus = (status?: string) => !!status && TERMINAL.includes(status);

/** 10s: a migration runs for tens of minutes, so this is about liveness, not precision. */
const POLL_MS = 10_000;

/**
 * Override just `x-tenant`, leaving the rest of the request context alone.
 *
 * Galaxy scopes both read endpoints on the authenticated `x-tenant` header and refuses a
 * caller-supplied tenant filter, so aiming these queries at another tenant means changing
 * the header — there is no query parameter for it and there deliberately shouldn't be.
 * `x-realm-id` is left as the context built it: neither endpoint reads it, and rebuilding
 * it here would pair the new tenant with the previous tenant's realm.
 */
const tenantHeader = (tenant?: string) =>
  tenant ? { headers: { 'x-tenant': tenant } } : undefined;

/**
 * Recent migrations for one tenant, polled while any of them is still running.
 *
 * `tenant` is NOT a filter the endpoint applies — it selects which tenant the request
 * authenticates as. It defaults to the tenant the admin is currently viewing, and the page
 * overrides it with the operator's chosen source tenant: a migration out of tenant B is
 * written against tenant B and is invisible to a list read as tenant A, so without this
 * the page would tell an operator to watch a run it could never show them.
 */
export const useRealmMigrations = (
  filters: { realmId?: string; teamId?: string; tenant?: string } = {},
) => {
  const { tenant, ...params } = filters;

  const query = useAppQuery<AxiosResponse<RealmMigrationSummary[]>>({
    queryKey: realmMigrationQueryKeys.list({
      identifier: 'useRealmMigrations',
      realmId: params.realmId,
      teamId: params.teamId,
      // In the key as well as the headers: the cache is per-tenant, and without this a
      // tenant switch would be served the previous tenant's rows.
      tenant,
    }),
    queryFn: ({ token, buildConfig }) =>
      listRealmMigrations({ params, token, config: buildConfig(tenantHeader(tenant)) }),
    // Stop once nothing is in flight. Without the stop condition this would poll a
    // finished list forever on an idle tab.
    refetchInterval: (query) => {
      const migrations = query.state.data?.data ?? [];
      const anyRunning = migrations.some((m) => !isTerminalMigrationStatus(m.status));
      return anyRunning ? POLL_MS : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  return {
    migrations: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * The step tree of one migration.
 *
 * Steps are keyed on row id rather than (action, service) because a resumed migration
 * appends a SECOND set of dump/load/verify children under the same root.
 *
 * `isRunning` must be derived from the polled list rather than from a row captured when
 * the operator clicked it: a snapshot's status never reaches a terminal value, so this
 * query would keep polling for as long as the page stayed open.
 */
export const useRealmMigrationSteps = (
  migrationLogId: number | null,
  isRunning: boolean,
  tenant?: string,
) => {
  const query = useAppQuery<AxiosResponse<RealmMigrationStep[]>>({
    queryKey: realmMigrationQueryKeys.detail({
      identifier: 'useRealmMigrationSteps',
      migrationLogId: migrationLogId ?? -1,
      tenant,
    }),
    queryFn: ({ token, buildConfig }) =>
      getRealmMigrationSteps({
        params: { migrationLogId: migrationLogId as number },
        token,
        // Galaxy re-verifies the row's tenant against this header, so it has to be the
        // same tenant the list was read as or the lookup 404s.
        config: buildConfig(tenantHeader(tenant)),
      }),
    enabled: migrationLogId !== null,
    refetchInterval: isRunning ? POLL_MS : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  /*
   * One last fetch when the migration finishes.
   *
   * The summary list has its own timer, so a migration that completes between two step
   * polls is reported terminal by the list first — which switches this interval off while
   * the cache still holds the last in-progress snapshot. The operator would be left
   * looking at a finished migration whose closing steps never arrived, Verify Checksums
   * among them, and nothing would refresh it: the interval is gone and a focused tab
   * fires no focus event.
   *
   * One fetch is enough because galaxy derives the summary's status FROM the child steps,
   * so by the time it reads terminal the steps are already written.
   */
  const { refetch } = query;
  const previous = useRef<{ id: number | null; running: boolean }>({ id: null, running: false });

  useEffect(() => {
    const { id: previousId, running: wasRunning } = previous.current;
    previous.current = { id: migrationLogId, running: isRunning };

    // Only the running -> terminal transition of the SAME migration; switching rows
    // starts a fresh query that fetches on its own.
    if (migrationLogId !== null && previousId === migrationLogId && wasRunning && !isRunning) {
      void refetch();
    }
  }, [migrationLogId, isRunning, refetch]);

  return {
    steps: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};
